// Modal built on native <dialog>: focus trapping, Escape and the inert
// background come from the browser.

import { useEffect, useId, useRef, type ReactNode } from 'react';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  /** Footer actions (buttons). */
  actions?: ReactNode;
  /** Room for richer content (e.g. a deal with its email thread). */
  wide?: boolean;
}

export function Dialog({ open, onClose, title, description, children, actions, wide }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // jsdom lacks showModal(); fall back to the open attribute there.
    if (open && !el.open) {
      if (typeof el.showModal === 'function') el.showModal();
      else el.setAttribute('open', '');
    } else if (!open && el.open) {
      if (typeof el.close === 'function') el.close();
      else el.removeAttribute('open');
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        // Clicks on the backdrop land on the <dialog> itself.
        if (e.target === ref.current) onClose();
      }}
      className={`${wide ? 'w-[min(48rem,calc(100vw-2rem))]' : 'w-[min(32rem,calc(100vw-2rem))]'} max-h-[calc(100vh-2rem)] overflow-y-auto rounded-xl border border-border bg-bg p-0 text-text shadow-xl`}
    >
      {open && (
        <div className="p-6 space-y-4">
          <div>
            <h2 id={titleId} className="text-lg font-semibold">
              {title}
            </h2>
            {description && <p className="mt-1 text-sm text-muted">{description}</p>}
          </div>
          {children}
          {actions && <div className="flex justify-end gap-2 pt-2">{actions}</div>}
        </div>
      )}
    </dialog>
  );
}
